import { ModalType } from '@components/MarketingModal/Content/MarketingCopy';
import { TiModalColorScheme } from '@components/TIModal';
import { subscribe_path } from '@routes';
import { AnyBlueconicEvent } from '@utils/blueconic/eventsInterfaces';
import {
  BlueconicCustomUrlModalProps,
  BlueconicDialogues,
  BlueconicLeadCaptureModalHorizontalArticlesProps,
  BlueconicLeadCaptureModalProps,
  BlueconicLeadCaptureModalSingleArticleProps,
  BlueconicLeadCaptureModalSplitImgProps,
  BlueconicLeadCaptureModalVerticalArticlesProps,
  BlueconicLeadCaptureModalWithMultipleSubscribeOptionsProps,
  BlueconicLeadCaptureModalWithNewsletterProps,
  BlueconicLeadCaptureModalWithNewslettersProps,
  BlueconicNewsletterModalProps,
  BlueconicRetentionP0Props,
  BlueconicRetentionP1Props,
  BlueconicRetentionP2Props,
  BlueconicRetentionP3Props,
  BlueconicRetentionP4Props,
} from '@utils/blueconic/propInterfaces';

import { BLUECONIC, BlueconicClickEvent } from './constants';

export const getDialogueNameByVariantId = (variantId: string) => {
  if (window.blueConicClient) {
    const interactionDetails = window.blueConicClient?.getInteractionNamesById?.(variantId);
    if (interactionDetails) {
      const dialogueName = interactionDetails.name;
      const variantName = interactionDetails.variantName;
      if (!dialogueName || !variantName) {
        return null;
      }
      return `${dialogueName}-${variantName}`;
    } else {
      console.warn('No interaction details found for variantId:', variantId);
    }
  }
  return null;
};

export const captureBlueconicClickEvent = (variantId?: string) => {
  if (variantId) {
    window.blueConicClient.createEvent(BlueconicClickEvent, variantId);
  }
};

export const getClassByModalType = (modalType: ModalType) => {
  switch (modalType) {
    case ModalType.LeadCaptureModalWithHorizontalArticles:
    case ModalType.RetentionP4:
      return 'max-h-[700px] md:h-auto';
    case ModalType.LeadCapture:
      return 'xs:top-1/4 md:top-1/2';
    default:
      return '';
  }
};

export const getBlueconicModalProps = (e: AnyBlueconicEvent) => {
  const dialogueType = e.data.dialogueType;
  const dialogueName = getDialogueNameByVariantId(e.variantId) || `blueconic-${e.variantId}`;

  switch (dialogueType) {
    case BlueconicDialogues.CustomUrlModal: {
      const customUrlEventData = e.data as BlueconicCustomUrlModalProps;
      return {
        ...customUrlEventData,
        customUrl: customUrlEventData.customUrl,
        modalType: ModalType.CustomUrl,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
      };
    }
    case BlueconicDialogues.NewsletterModal: {
      const newsletterData = e.data as BlueconicNewsletterModalProps;
      return {
        ...e.data,
        subtitle: newsletterData.newsletterDescription,
        title: `${newsletterData.newsletterName} by ${newsletterData.authors}`,
        disclosure: newsletterData.frequency,
        offerLink: newsletterData.redirectUri || subscribe_path,
        modalType: ModalType.LeadCapture,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
      };
    }
    case BlueconicDialogues.AppDownload:
      return {
        ...e.data,
        id: `blueconicModal-${e.variantId}`,
        modalType: ModalType.AppDialog,
        externalSourceId: e.variantId,
        name: dialogueName,
      };
    case BlueconicDialogues.LeadCaptureModal: {
      const leadData = e.data as BlueconicLeadCaptureModalProps;
      return {
        ...e.data,
        title: leadData.title,
        subtitle: leadData.subtitle,
        highlight: leadData.highlight,
        disclosure: leadData.disclosure,
        buttonText: leadData.buttonText,
        modalType: ModalType.LeadCapture,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
        offerLink: subscribe_path({ offer: leadData.offerCode }),
      };
    }
    case BlueconicDialogues.LeadCaptureModalWithVerticalArticles: {
      const leadData = e.data as BlueconicLeadCaptureModalVerticalArticlesProps;
      return {
        ...e.data,
        highlight: leadData.highlight,
        title: leadData.title,
        subtitle: leadData.subtitle,
        buttonText: leadData.buttonText,
        modalType: ModalType.LeadCaptureModalWithVerticalArticles,
        modalTheme: TiModalColorScheme.light,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
        offerLink: subscribe_path({ offer: leadData.offerCode }),
        offerCode: leadData.offerCode,
        articleSlugs: leadData.articleSlugs,
        articleTitleSection: leadData.articleTitleSection,
      };
    }
    case BlueconicDialogues.LeadCaptureModalWithHorizontalArticles: {
      const leadData = e.data as BlueconicLeadCaptureModalHorizontalArticlesProps;
      return {
        ...e.data,
        title: leadData.title,
        subtitle: leadData.subtitle,
        buttonText: leadData.buttonText,
        modalType: ModalType.LeadCaptureModalWithHorizontalArticles,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
        offerLink: subscribe_path({ offer: leadData.offerCode }),
        offerCode: leadData.offerCode,
        articleSlugs: leadData.articleSlugs,
        modalTheme: TiModalColorScheme.light,
      };
    }
    case BlueconicDialogues.LeadCaptureModalWithSingleArticle: {
      const leadData = e.data as BlueconicLeadCaptureModalSingleArticleProps;
      return {
        ...e.data,
        highlight: leadData.highlight,
        title: leadData.title,
        subtitle: leadData.subtitle,
        buttonText: leadData.buttonText,
        modalType: ModalType.LeadCaptureWithSingleArticle,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
        offerLink: subscribe_path({ offer: leadData.offerCode }),
        offerCode: leadData.offerCode,
        articleSlugs: [leadData.articleSlug],
      };
    }
    case BlueconicDialogues.LeadCaptureModalWithSplitImg: {
      const leadData = e.data as BlueconicLeadCaptureModalSplitImgProps;
      return {
        ...e.data,
        highlight: leadData.highlight,
        buttonText: leadData.buttonText,
        modalType: ModalType.LeadCaptureWithSplitImg,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
        offerLink: subscribe_path({ offer: leadData.offerCode }),
        offerCode: leadData.offerCode,
        modalTheme: TiModalColorScheme.light,
        imgPathLarge: leadData.imgPathLarge,
        imgPathMedium: leadData.imgPathMedium,
        imgPathSmall: leadData.imgPathSmall,
        modalContainerClass: 'min-h-[420px]',
      };
    }
    case BlueconicDialogues.LeadCaptureModalWithNewsletters: {
      const leadData = e.data as BlueconicLeadCaptureModalWithNewslettersProps;
      return {
        ...e.data,
        modalType: ModalType.LeadCaptureModalWithNewsletters,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
        newsletterIds: leadData.newsletterIds,
        title: leadData.title,
        subtitle: leadData.subtitle,
        offerLink: subscribe_path({ offer: leadData.offerCode }),
        offerCode: leadData.offerCode,
        buttonText: leadData.buttonText,
        leadButtonText: leadData.leadButtonText,
        leadType: leadData.leadType,
        modalTheme: TiModalColorScheme.light,
      };
    }
    case BlueconicDialogues.LeadCaptureModalWithNewsletter: {
      const leadData = e.data as BlueconicLeadCaptureModalWithNewsletterProps;
      return {
        ...e.data,
        modalType: ModalType.LeadCaptureModalWithNewsletter,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        name: dialogueName,
        newsletterId: leadData.newsletterId,
        offerLink: subscribe_path({ offer: leadData.offerCode }),
        offerCode: leadData.offerCode,
        buttonText: leadData.buttonText,
        leadType: leadData.leadType,
        modalTheme: TiModalColorScheme.light,
      };
    }
    case BlueconicDialogues.LeadCaptureModalWithMultipleSubscribeOptions: {
      const leadData = e.data as BlueconicLeadCaptureModalWithMultipleSubscribeOptionsProps;
      return {
        ...e.data,
        modalType: ModalType.LeadCaptureModalWithMultipleSubscribeOptions,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        modalTheme: TiModalColorScheme.light,
        leadType: leadData.leadType,
        title: leadData.title,
        subtitle: leadData.subtitle,
        buttonText: leadData.buttonText,
        highlight: leadData.highlight,
        proOfferCode: leadData.proOfferCode,
        annualOfferCode: leadData.annualOfferCode,
      };
    }
    case BlueconicDialogues.RetentionP0ContentSmall: {
      const retentionData = e.data as BlueconicRetentionP0Props;
      return {
        ...retentionData,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        modalType: ModalType.RetentionP0,
        modalTheme: TiModalColorScheme.light,
        name: dialogueName,
      };
    }
    case BlueconicDialogues.RetentionP1SingleContentHighlightText: {
      const retentionData = e.data as BlueconicRetentionP1Props;
      return {
        ...retentionData,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        modalType: ModalType.RetentionP1,
        name: dialogueName,
      };
    }
    case BlueconicDialogues.RetentionP2SingleContentHighlightImage: {
      const retentionData = e.data as BlueconicRetentionP2Props;
      return {
        ...retentionData,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        modalType: ModalType.RetentionP2,
        name: dialogueName,
      };
    }
    case BlueconicDialogues.RetentionP3MultipleContentText: {
      const retentionData = e.data as BlueconicRetentionP3Props;
      return {
        ...retentionData,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        modalType: ModalType.RetentionP3,
        modalTheme: retentionData.modalTheme || TiModalColorScheme.light,
        name: dialogueName,
      };
    }
    case BlueconicDialogues.RetentionP4MultipleContentImage: {
      const retentionData = e.data as BlueconicRetentionP4Props;
      return {
        ...retentionData,
        externalSource: BLUECONIC,
        externalSourceId: e.variantId,
        modalType: ModalType.RetentionP4,
        modalTheme: TiModalColorScheme.light,
        name: dialogueName,
      };
    }
    default:
      return null;
  }
};
